import { convertToDefaultCurrency } from '../state/zustand';
import type { WorldscaleFreight, Location } from '../graphql';
import type { ScenarioLocation } from './planningData';

export type WorldscaleFreightRate = Pick<
  WorldscaleFreight,
  'rate' | 'currency' | 'year'
> & {
  locationByFromLocationId?: Pick<Location, 'id' | 'name' | 'country'> | null;
  locationByToLocationId?: Pick<Location, 'id' | 'name' | 'country'> | null;
};

export type WorldscaleFreightRateMap = Map<string, WorldscaleFreightRate>;

/**
 * Builds a map `${fromLocationName}-${toLocationName}` => WorldscaleFreightRate from worldscale freights
 */
export function buildWorldscaleFreightRateMap(
  worldscaleFreights?: (WorldscaleFreightRate | null)[],
): WorldscaleFreightRateMap {
  const map: WorldscaleFreightRateMap = new Map();

  if (!worldscaleFreights) {
    return map;
  }

  for (const freight of worldscaleFreights) {
    if (!freight) continue;

    const key = `${freight.locationByFromLocationId?.name}-${freight.locationByFromLocationId?.country}-${freight.locationByToLocationId?.name}-${freight.locationByToLocationId?.country}`;
    map.set(key, {
      locationByFromLocationId: freight.locationByFromLocationId,
      locationByToLocationId: freight.locationByToLocationId,
      rate: Number(freight.rate),
      currency: freight.currency,
      year: freight.year,
    });
  }

  return map;
}

export type OrderPorts = {
  locationByLoadPortId?: {
    name?: ScenarioLocation['name'] | null;
    country?: ScenarioLocation['country'] | null;
  } | null;
  locationByDischargePortId?: {
    name?: ScenarioLocation['name'] | null;
    country?: ScenarioLocation['country'] | null;
  } | null;
} | null;

export function calculateWorldscaleFreight(
  freightPercentage: number,
  worldscaleFreightRates?: WorldscaleFreightRateMap,
  order?: OrderPorts,
  mass?: number | null,
): { freight: number; flatRate: number } | null {
  if (!worldscaleFreightRates || !order) {
    console.error('Worldscale freight rates or order not provided', {
      worldscaleFreightRates,
      order,
    });
    return null;
  }

  const loadPortName = order.locationByLoadPortId?.name;
  const loadPortCountry = order.locationByLoadPortId?.country;
  const dischargePortName = order.locationByDischargePortId?.name;
  const dischargePortCountry = order.locationByDischargePortId?.country;

  if (!loadPortName || !dischargePortName) {
    console.error('Missing port names for worldscale calculation');
    return null;
  }

  if (!loadPortCountry || !dischargePortCountry) {
    console.error('Missing port countries for worldscale calculation');
    console.log('ORDER', order);
    return null;
  }

  const key = `${loadPortName}-${loadPortCountry}-${dischargePortName}-${dischargePortCountry}`;
  const worldscaleRate = worldscaleFreightRates.get(key);

  if (!worldscaleRate) {
    console.warn(
      `No worldscale freight rate found for route ${loadPortName} -> ${dischargePortName}`,
    );
    return null;
  }

  const wsMultiplier = freightPercentage / 100;
  const actualFreightRate = Number(worldscaleRate.rate) * wsMultiplier;

  const convertedFreightRate = worldscaleRate.currency
    ? convertToDefaultCurrency(actualFreightRate, worldscaleRate.currency)
    : actualFreightRate;

  if (convertedFreightRate === null) {
    return null;
  }

  const freightWithMass = mass
    ? convertedFreightRate * mass
    : convertedFreightRate;

  return {
    freight: freightWithMass,
    flatRate: Number(worldscaleRate.rate),
  };
}
